import { useTranslation } from 'react-i18next'
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { useYearlyProgress } from '@/hooks/dashboard/useYearlyProgress'
import { ChartContainer } from '../ChartContainer'

// Types for Recharts custom components
interface TooltipPayload {
  value: number
  payload: {
    booksRead: number
    cumulative: number
    monthName: string
  }
}

interface CustomTooltipProps {
  active?: boolean
  payload?: TooltipPayload[]
  label?: string
}

export function ProgressChart() {
  const { t } = useTranslation()
  const { monthlyData, currentYear, totalReadThisYear, isLoading, error } = useYearlyProgress()

  const title = t('dashboard.charts.yearlyProgress')
  const description = `${totalReadThisYear} ${t('dashboard.charts.booksReadIn')} ${currentYear}`

  // Custom tooltip to show detailed information
  const CustomTooltip = ({ active, payload, label }: CustomTooltipProps) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload
      return (
        <div className="bg-background border border-border rounded-lg p-3 shadow-lg">
          <p className="font-medium text-foreground mb-1">{`${label} ${currentYear}`}</p>
          <div className="space-y-1">
            <p className="text-sm text-foreground">
              <span
                className="inline-block w-3 h-3 rounded-full mr-2"
                style={{ backgroundColor: '#06B6D4' }}
              />
              {`${data.booksRead} ${t('dashboard.charts.booksThisMonth')}`}
            </p>
            <p className="text-sm text-foreground">
              <span
                className="inline-block w-3 h-3 rounded-full mr-2"
                style={{ backgroundColor: '#10B981' }}
              />
              {`${data.cumulative} ${t('dashboard.charts.totalSoFar')}`}
            </p>
          </div>
        </div>
      )
    }
    return null
  }

  return (
    <ChartContainer
      title={title}
      description={description}
      isLoading={isLoading}
      error={error?.message || null}
    >
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart
          data={monthlyData}
          margin={{
            top: 20,
            right: 30,
            left: 20,
            bottom: 5,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
          <XAxis
            dataKey="monthName"
            axisLine={false}
            tickLine={false}
            className="text-xs text-muted-foreground"
          />
          <YAxis axisLine={false} tickLine={false} className="text-xs text-muted-foreground" />
          <Tooltip content={<CustomTooltip />} />

          {/* Bars for books read per month */}
          <Bar dataKey="booksRead" fill="#06B6D4" radius={[2, 2, 0, 0]} opacity={0.7} />

          {/* Cumulative line */}
          <Line
            type="monotone"
            dataKey="cumulative"
            stroke="#10B981"
            strokeWidth={3}
            dot={{
              r: 4,
              fill: '#10B981',
              strokeWidth: 2,
              stroke: 'hsl(var(--background))',
            }}
            activeDot={{
              r: 6,
              fill: '#10B981',
              strokeWidth: 2,
              stroke: 'hsl(var(--background))',
            }}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </ChartContainer>
  )
}
